import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  getNanoVariantPetGenderSelect,
  getNanoVariantPetName,
  hasNanoVariantPetName,
} from '../../../lib/util/petUrlParams';
import { Species } from '../../../types/gql-op-types';
import styles from './Rescue.module.scss';

/**
 * Adopters arrive believing the microchip the rescue just fitted was the safety step, so the page has
 * to draw the distinction before anything else lands. Framed as complementary - the chip does a real
 * job, it just is not an alert.
 *
 * The found-dogs stat rides inside the module as its proof row: the claim and the number that backs
 * it read as one argument. The figure is static by design - verified at 201,764 lost-pet sessions
 * turned back off - and the rounded value is the claim we can actually stand behind.
 *
 * The figure counts dogs only, so cat adopters get the module without it rather than a number that
 * is not about their pet.
 */
export default function RescueChipModule({ species }: { species: Species }) {
  const { t } = useTranslation();

  return (
    <section className={styles.chipModule}>
      <div className={styles.chipInner}>
        {/* named variant only while the adopter link's pet params are in the url */}
        <h2 className={styles.chipTitle}>
          {hasNanoVariantPetName()
            ? t('rescue_chip_title_named', {
                petName: getNanoVariantPetName(),
                gender: getNanoVariantPetGenderSelect(),
              })
            : t('rescue_chip_title')}
        </h2>
        <p className={styles.chipBody}>{t('rescue_chip_body')}</p>
        {species !== Species.CAT && (
          <div className={styles.chipStat}>
            <span className={styles.chipStatValue}>{t('rescue_stat_value')}</span>
            <span className={styles.chipStatLabel}>{t('rescue_stat_label')}</span>
          </div>
        )}
      </div>
    </section>
  );
}
